import React from 'react';
import {
    Button,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import { HeartFill, HeartLine } from '@archipro-design/icons';
import * as S from './SamplePurchase.styles';
import {
    ButtonBase,
    ButtonHeart,
    SaveToDesignBoard,
} from '../variant-purchase/QuantityGroup.style';
import { useSampleStock } from '../../hook/use-sample-stock';
import Delivery from '../variant-purchase/Delivery';
import ClickAndCollect from '../variant-purchase/ClickAndCollect';
import ShippingPopup from '@modules/ecommerce/component/shipping-popup/ShippingPopup';
import { useAddToCart } from '@modules/ecommerce/hook/use-add-to-cart';
import { useVariantSelectionState } from '../../context/VariantSelectionContext';
import LastAddedPopup from '~/modules/ecommerce/component/shopping-cart-popup/LastAddedPopup.mobile';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import { FollowSelector } from '~/modules/moodboards/components';
import { useTracker } from '@archipro-website/tracker';
import { useCartPopupState } from '~/modules/ecommerce/context/CartPopupContext';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import { WhereToBuyButton } from '../product-details/WhereToBuyButton';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

interface SamplePurchaseProps {
    productId: number;
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
}

const SamplePurchase = ({
    productId,
    whereToBuyEnabled,
    onClickWhereToBuy,
}: SamplePurchaseProps) => {
    const { mobile } = useAppDisplayModeContext();
    const { selectedVariant } = useVariantSelectionState();
    const { priceText, canPurchase } = useSampleStock(selectedVariant);
    const favouriteItem = useFavouriteVariant(productId);
    const { isInDesignBoard } = useHandleFollow();
    const [modalOpen, setModalOpen] = React.useState(false);
    const [addToCart, fetcher] = useAddToCart(true);
    const [lastItemAdded, setLastItemAdded] = React.useState<CartItem | null>(
        null,
    );
    const { open: cartPopupOpen, onChange: setCartPopupOpen } =
        useCartPopupState();
    const { toast } = useToaster();
    const { css } = useStyles();
    const {
        siteVariables: { colors },
    } = useTheme();
    const tracker = useTracker();
    const onRegionClick = () => {
        setModalOpen(true);
    };
    const onSamplePurchase = () => {
        if (selectedVariant) {
            addToCart(selectedVariant, 1);
        }
    };

    React.useEffect(() => {
        if (fetcherIsDone(fetcher)) {
            if (fetcher.data && 'error' in fetcher.data) {
                toast(fetcher.data.message || 'Unable to add sample to cart', {
                    type: 'error',
                });
            } else {
                const lastItem =
                    fetcher.data?.addToShoppingCart.LastAddedItems[0];
                if (lastItem) {
                    setLastItemAdded({ ...lastItem, Quantity: 1 });
                }
            }
        }
    }, [fetcher, toast]);

    return (
        <div className={`${css(S.SamplePurchase)} sample-purchase`}>
            {!mobile && (
                <div className={css(S.ShippingGroup)}>
                    <Delivery isSample={true} onRegionClick={onRegionClick} />
                    <ClickAndCollect
                        isSample={true}
                        onLocationClick={() => setModalOpen(true)}
                    />
                </div>
            )}
            {mobile ? (
                <>
                    <div className={css(S.OrderGroup)}>
                        <Button
                            className={css(S.OrderSampleButtonMobile)}
                            size={16}
                            fluid
                            loading={fetcher.state !== 'idle'}
                            onClick={() => {
                                tracker.log('OrderSampleSelect', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData:
                                            selectedVariant?.ID || productId,
                                    },
                                });
                                setModalOpen(true);
                            }}
                        >
                            Order Sample ({priceText})
                        </Button>
                        <FollowSelector
                            trigger={
                                <Button
                                    className={css(
                                        ButtonBase,
                                        ButtonHeart,
                                        SaveToDesignBoard,
                                    )}
                                    transparent
                                    size={20}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                    icon={
                                        isInDesignBoard(
                                            favouriteItem.pinId,
                                            favouriteItem.pinType,
                                        ) ? (
                                            <HeartFill size="2x" />
                                        ) : (
                                            <HeartLine size="2x" />
                                        )
                                    }
                                    variables={{
                                        primaryButtonBackgroundColorNormal:
                                            colors.white['100A'],
                                        iconPadding: 0,
                                        buttonPadding: 0,
                                    }}
                                />
                            }
                            pinId={favouriteItem.pinId}
                            pinType={favouriteItem.pinType}
                        />
                    </div>
                    {whereToBuyEnabled && (
                        <WhereToBuyButton onClick={onClickWhereToBuy} />
                    )}
                </>
            ) : (
                <div className={css(S.OrderGroup)}>
                    <Button
                        className={css(S.OrderSample)}
                        aria-hidden={!canPurchase}
                        size={16}
                        loading={fetcher.state !== 'idle'}
                        onClick={onSamplePurchase}
                    >
                        Order Sample ({priceText})
                    </Button>
                    <FollowSelector
                        trigger={
                            <Button
                                className={css(
                                    ButtonBase,
                                    ButtonHeart,
                                    SaveToDesignBoard,
                                )}
                                transparent
                                size={20}
                                onClick={(event) => {
                                    event.stopPropagation();
                                }}
                                icon={
                                    isInDesignBoard(
                                        favouriteItem.pinId,
                                        favouriteItem.pinType,
                                    ) ? (
                                        <HeartFill size="2x" />
                                    ) : (
                                        <HeartLine size="2x" />
                                    )
                                }
                                variables={{
                                    primaryButtonBackgroundColorNormal:
                                        colors.white['100A'],
                                    iconPadding: 0,
                                    buttonPadding: 0,
                                }}
                            />
                        }
                        pinId={favouriteItem.pinId}
                        pinType={favouriteItem.pinType}
                    />
                </div>
            )}
            <ShippingPopup
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onConfirm={() => {
                    if (mobile) {
                        onSamplePurchase();
                    }
                    setModalOpen(false);
                }}
                isSample={true}
                confirmButtonText={'Confirm'}
            />
            {mobile && (
                <LastAddedPopup
                    open={Boolean(cartPopupOpen && lastItemAdded)}
                    item={lastItemAdded}
                    onOpenChange={(_, props) =>
                        props && !props.open && setCartPopupOpen(false)
                    }
                />
            )}
        </div>
    );
};

export default SamplePurchase;
